import React, { useEffect, useState } from 'react';
import { config } from '../config';

interface FrequentPerson {
    person_name: string;
    person_email: string | null;
    usage_count: number;
    last_used: string;
}

interface FrequentPeopleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectPerson: (name: string, email: string | null) => void;
    getAuthToken: () => Promise<string | null>;
}

const FrequentPeopleModal: React.FC<FrequentPeopleModalProps> = ({
    isOpen,
    onClose,
    onSelectPerson,
    getAuthToken
}) => {
    const [people, setPeople] = useState<FrequentPerson[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            loadFrequentPeople();
        }
    }, [isOpen]);

    const loadFrequentPeople = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = await getAuthToken();
            if (!token) {
                setError('Nie ste prihlásený');
                setLoading(false);
                return;
            }

            const response = await fetch(`${config.apiUrl}/getFrequentSplitPeople`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': token
                }
            });

            if (!response.ok) {
                throw new Error('Nepodarilo sa načítať osoby');
            }

            const data = await response.json();
            setPeople(data.people || []);
        } catch (err: any) {
            console.error('Error loading frequent people:', err);
            setError(err.message || 'Nepodarilo sa načítať žiadne osoby.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectPerson = (person: FrequentPerson) => {
        onSelectPerson(person.person_name, person.person_email);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 5000,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                overflowY: 'scroll',
                WebkitOverflowScrolling: 'touch',
                paddingBottom: '100px'
            }}
            onClick={onClose}
        >
            <div
                style={{
                    maxWidth: '500px',
                    width: '100%',
                    margin: '40px 20px 40px 20px',
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    position: 'relative'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '24px 24px 16px 24px',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827' }}>
                            Časté osoby
                        </h2>
                        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                            Osoby, s ktorými ste už rozdeľovali bločky
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#6b7280',
                            padding: '4px',
                            lineHeight: 1
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: '24px' }}>
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                            <div style={{ marginBottom: '12px' }}>⏳</div>
                            <div>Načítavam...</div>
                        </div>
                    )}

                    {error && (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: '#dc2626',
                                background: '#fef2f2',
                                borderRadius: '12px',
                                border: '1px solid #fecaca'
                            }}
                        >
                            <div style={{ marginBottom: '8px', fontSize: '32px' }}>⚠️</div>
                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Chyba</div>
                            <div style={{ fontSize: '14px' }}>{error}</div>
                        </div>
                    )}

                    {!loading && !error && people.length === 0 && (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: '#6b7280',
                                background: '#f9fafb',
                                borderRadius: '12px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            <div style={{ marginBottom: '12px', fontSize: '48px' }}>📭</div>
                            <div style={{ fontSize: '14px' }}>
                                Nenašli sme žiadnych ľudí, s ktorými ste rozdeľovali bločky.
                            </div>
                        </div>
                    )}

                    {!loading && !error && people.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {people.map((person, index) => (
                                <button
                                    key={`${person.person_name}-${person.person_email}-${index}`}
                                    onClick={() => handleSelectPerson(person)}
                                    style={{
                                        background: '#fff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'all 0.2s',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#f9fafb';
                                        e.currentTarget.style.borderColor = '#667eea';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#fff';
                                        e.currentTarget.style.borderColor = '#e5e7eb';
                                    }}
                                >
                                    <div style={{ fontWeight: 600, fontSize: '16px', color: '#111827' }}>
                                        {person.person_name}
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                                        {person.person_email || 'Bez emailu'}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FrequentPeopleModal;
